import { Controller } from "@hotwired/stimulus"
import { prefersReducedMotion } from "../lib/motion"

/**
 * Ads Category Rotator Controller
 * Animates rotating business category names with icons for /ads CTA card
 *
 * Features:
 * - Icon + text rotation together (innerHTML, not textContent)
 * - Slide-up animation reusing existing CSS classes
 * - Pause on hover/focus for accessibility
 * - Respects prefers-reduced-motion
 *
 * Usage:
 * <div data-controller="ads-category-rotator"
 *      data-ads-category-rotator-interval-value="2000">
 *   <span data-ads-category-rotator-target="slot">
 *     <!-- Initial content with icon -->
 *   </span>
 * </div>
 */
export default class extends Controller {
  static targets = ["slot"]
  static values = {
    interval: { type: Number, default: 2000 },
    firstDelay: { type: Number, default: 1000 }
  }

  // Taiwan popular business categories with official Tabler icons (https://tabler.io/icons)
  // Order: Café, Hair Salon, Restaurant, Yoga Studio, Online Store, Nail Salon, Drink Shop, Repair Shop
  categories = [
    {
      name: "咖啡廳",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="inline-block w-7 h-7 mr-1.5 align-baseline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 14c.83 .642 2.077 1.017 3.5 1c1.423 .017 2.67 -.358 3.5 -1c.83 -.642 2.077 -1.017 3.5 -1c1.423 -.017 2.67 .358 3.5 1"/><path d="M8 3a2.4 2.4 0 0 0 -1 2a2.4 2.4 0 0 0 1 2"/><path d="M12 3a2.4 2.4 0 0 0 -1 2a2.4 2.4 0 0 0 1 2"/><path d="M3 10h14v5a6 6 0 0 1 -6 6h-2a6 6 0 0 1 -6 -6v-5z"/><path d="M16.746 16.726a3 3 0 1 0 .252 -5.555"/></svg>`
    },
    {
      name: "美髮店",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="inline-block w-7 h-7 mr-1.5 align-baseline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 7a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/><path d="M3 17a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/><path d="M8.6 8.6l10.4 10.4"/><path d="M8.6 15.4l10.4 -10.4"/></svg>`
    },
    {
      name: "餐廳",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="inline-block w-7 h-7 mr-1.5 align-baseline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M19 3v12h-5c-.023 -3.681 .184 -7.406 5 -12m0 12v6h-1v-3m-10 -14v17m-3 -17v3a3 3 0 1 0 6 0v-3"/></svg>`
    },
    {
      name: "瑜伽教室",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="inline-block w-7 h-7 mr-1.5 align-baseline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M11 4a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M4 20h4l1.5 -3"/><path d="M17 20l-1 -5h-5l1 -7"/><path d="M4 10l4 -1l4 -1l4 1.5l4 1.5"/></svg>`
    },
    {
      name: "網路商店",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="inline-block w-7 h-7 mr-1.5 align-baseline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M19.5 7a9 9 0 0 0 -7.5 -4a8.991 8.991 0 0 0 -7.484 4" /><path d="M11.5 3a16.989 16.989 0 0 0 -1.826 4" /><path d="M12.5 3a16.989 16.989 0 0 1 1.828 4" /><path d="M19.5 17a9 9 0 0 1 -7.5 4a8.991 8.991 0 0 1 -7.484 -4" /><path d="M11.5 21a16.989 16.989 0 0 1 -1.826 -4" /><path d="M12.5 21a16.989 16.989 0 0 0 1.828 -4" /><path d="M2 10l1 4l1.5 -4l1.5 4l1 -4" /><path d="M17 10l1 4l1.5 -4l1.5 4l1 -4" /><path d="M9.5 10l1 4l1.5 -4l1.5 4l1 -4" /></svg>`
    },
    {
      name: "美甲店",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="inline-block w-7 h-7 mr-1.5 align-baseline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M16 18a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2m0 -12a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2m-7 12a6 6 0 0 1 6 -6a6 6 0 0 1 -6 -6a6 6 0 0 1 -6 6a6 6 0 0 1 6 6" /></svg>`
    },
    {
      name: "飲料店",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="inline-block w-7 h-7 mr-1.5 align-baseline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 11h14v-3h-14l0 3"/><path d="M17.5 11l-1.5 10h-8l-1.5 -10"/><path d="M6 8v-1a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v1"/><path d="M15 5v-2"/></svg>`
    },
    {
      name: "修理店",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="inline-block w-7 h-7 mr-1.5 align-baseline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 10h3v-3l-3.5 -3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1 -3 3l-6 -6a6 6 0 0 1 -8 -8l3.5 3.5"/></svg>`
    }
  ]

  connect() {
    this.currentIndex = 0
    this.isAnimating = false
    this.isPaused = false

    // Check for reduced motion preference
    this.prefersReducedMotion = prefersReducedMotion()

    // Pause on hover/focus for accessibility
    this.element.addEventListener("mouseenter", this.pause)
    this.element.addEventListener("mouseleave", this.resume)
    this.element.addEventListener("focusin", this.pause)
    this.element.addEventListener("focusout", this.resume)

    // Show first category
    this.showCategory(this.categories[0])

    // Start rotation
    if (this.categories.length > 1) {
      this.startRotation()
    }
  }

  disconnect() {
    this.stopRotation()
    this.element.removeEventListener("mouseenter", this.pause)
    this.element.removeEventListener("mouseleave", this.resume)
    this.element.removeEventListener("focusin", this.pause)
    this.element.removeEventListener("focusout", this.resume)
  }

  startRotation() {
    // Fire the first rotation sooner so the card visibly animates on load — removes the ~2s
    // static pause from setInterval's first-fire delay — then settle into the steady cadence.
    this.firstTimer = setTimeout(() => {
      if (!this.isPaused && !this.isAnimating) {
        this.rotateToNext()
      }
      this.rotationTimer = setInterval(() => {
        if (!this.isPaused && !this.isAnimating) {
          this.rotateToNext()
        }
      }, this.intervalValue)
    }, this.firstDelayValue)
  }

  stopRotation() {
    if (this.firstTimer) {
      clearTimeout(this.firstTimer)
      this.firstTimer = null
    }
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer)
      this.rotationTimer = null
    }
  }

  pause = () => {
    this.isPaused = true
  }

  resume = () => {
    this.isPaused = false
  }

  rotateToNext() {
    const nextIndex = (this.currentIndex + 1) % this.categories.length
    const nextCategory = this.categories[nextIndex]

    if (this.prefersReducedMotion) {
      // No animation for reduced motion preference
      this.showCategory(nextCategory)
      this.currentIndex = nextIndex
    } else {
      this.animateTransition(nextCategory, nextIndex)
    }
  }

  showCategory(category) {
    if (this.hasSlotTarget) {
      this.slotTarget.innerHTML = `${category.icon}<span>${category.name}</span>`
    }
  }

  animateTransition(nextCategory, nextIndex) {
    if (this.isAnimating || !this.hasSlotTarget) return

    this.isAnimating = true
    const slot = this.slotTarget

    // Slide out current category
    slot.classList.remove("animate-category-in")
    slot.classList.add("animate-category-out")

    // After slide-out animation completes, change content and slide in
    setTimeout(() => {
      slot.innerHTML = `${nextCategory.icon}<span>${nextCategory.name}</span>`
      slot.classList.remove("animate-category-out")
      slot.classList.add("animate-category-in")

      this.currentIndex = nextIndex

      // Animation complete
      setTimeout(() => {
        this.isAnimating = false
      }, 250) // Match slide-up-in duration
    }, 200) // Match slide-up-out duration
  }
}
